// ============================================================================
// DOOR (Swing door for house exits and room dividers)
// Kinematic door with two states: open and closed
// Uses physics to push player out of the way during animation
// ============================================================================

import CONFIG from '../config.js';
import { lerp, easeOutElastic } from '../utils/helpers.js';
import { DetachedDoor } from './DetachedDoor.js';
import { BreakableEntity } from './BreakableEntity.js';

export class Door extends BreakableEntity {
    constructor(x, y, width, height, hingeSide = 'left', physics = null, orientation = 'horizontal') {
        super(x, y, width, height, physics);
        this.closedX = x;
        this.closedY = y;
        this.hingeSide = hingeSide; // 'left' or 'right' (relative to door's front)
        this.orientation = orientation; // 'horizontal' (top/bottom exits) or 'vertical' (side exits)
        
        // Door has two visual parts (left and right panels) - always uses hinged mechanism
        // Visual sprite size is always 50px total (25px per panel), independent of collision body
        this.visualDoorTotalWidth = 50; // Total visual width of both door panels
        this.visualDoorWidth = 25; // Each door panel visual width (half of total)
        
        // For horizontal doors: width is horizontal, height is vertical
        // For vertical doors: width is vertical (tall), height is horizontal (wide)
        if (orientation === 'horizontal') {
            this.doorWidth = width / 2; // Each door panel collision width (for physics calculations)
            this.doorHeight = height;
        } else {
            // Vertical doors: width is vertical dimension, height is horizontal dimension
            this.doorWidth = height / 2; // Each door panel collision width (for physics calculations)
            this.doorHeight = width; // Full vertical dimension
        }
        
        this.isOpen = false;
        this.doorAngle = 0; // Opening angle (0 = closed, 70 = open)
        this.targetAngle = 70; // Max opening angle for hinged doors
        this.lastIsOpenState = false; // Track state to detect changes
        
        if (physics) {
            this.body = physics.createRectangle(x, y, width, height, {
                isStatic: true,
                friction: 0.5,
                restitution: 0,
                isSensor: false // Start as colliding body
            });
            physics.linkEntity(this, this.body);
            
            // Override sensor: position at center of door (same as collision body center), 30% bigger than default
            if (this.sensorBody) {
                physics.removeBody(this.sensorBody);
                this.sensorBody = null;
            }
            const sensorRadius = (CONFIG.interactionRadius || 50) * 0.8 * 1.32; // 32% bigger than default (was 20%, now 10% bigger = 1.2 * 1.1)
            // Position sensor at the same center as the collision body (x, y)
            this.sensorBody = physics.createSensor(x, y, sensorRadius);
            physics.linkEntity(this, this.sensorBody);
        }
    }

    canInteract() {
        return !this.isAnimating;
    }

    performAction(level, aggression, game) {
        this.isOpen = !this.isOpen;
        this.targetAngle = this.isOpen ? 70 : 0; // Hinged door: 70 degrees max

        this.isAnimating = true;
        this.animationTime = 0;
        this.animationDuration = CONFIG.doorOpenSpeed[level];
        this.overshoot = level > 0 ? (this.targetAngle > 0 ? CONFIG.doorOvershoot[level] : -CONFIG.doorOvershoot[level]) : 0;
    }

    getBreakThreshold() {
        return CONFIG.doorBreakThreshold;
    }

    onBreak(game) {
        // Door breaks into two detached doors (left and right panels)
        // Calculate positions for left and right door panels
        const leftDoorX = this.x - this.visualDoorTotalWidth / 4; // Left panel center
        const rightDoorX = this.x + this.visualDoorTotalWidth / 4; // Right panel center
        
        // Each detached door is the size of one panel
        const doorPanelWidth = this.visualDoorWidth; // 25px per panel
        const doorPanelHeight = this.orientation === 'horizontal' ? this.height : this.width;
        
        // Cap force amount for spawn velocity (prevents tunneling), but damage was already applied uncapped
        const cappedForce = Math.min(this.lastDamageAmount, CONFIG.throwableMaxAggression);
        
        // Spawn left door panel
        game.detachedDoors.push(new DetachedDoor(
            leftDoorX,
            this.y,
            doorPanelWidth,
            doorPanelHeight,
            CONFIG.colors.cupboard,
            true, // isLeftDoor
            cappedForce,
            this.physics
        ));
        
        // Spawn right door panel
        game.detachedDoors.push(new DetachedDoor(
            rightDoorX,
            this.y,
            doorPanelWidth,
            doorPanelHeight,
            CONFIG.colors.cupboard,
            false, // isLeftDoor (right door)
            cappedForce,
            this.physics
        ));
    }

    update(dt) {
        if (this.isBroken) return;
        
        // When door state changes (open/close), destroy and recreate body
        // This forces Matter.js to clear collision pairs immediately
        if (this.body && this.isOpen !== this.lastIsOpenState) {
            // Store body properties
            const x = this.x;
            const y = this.y;
            const width = this.width;
            const height = this.height;
            
            // Remove old body
            this.physics.removeBody(this.body);
            this.body = null;
            
            // Create new body with correct sensor state
            this.body = this.physics.createRectangle(x, y, width, height, {
                isStatic: true,
                friction: 0.5,
                restitution: 0,
                isSensor: this.isOpen // Sensor when open, colliding when closed
            });
            this.physics.linkEntity(this, this.body);
            
            // Update tracked state
            this.lastIsOpenState = this.isOpen;
        } else if (this.body && !this.isOpen) {
            // When closed, ensure body is at correct position
            this.physics.setPosition(this.body, this.x, this.y);
            this.physics.setAngle(this.body, 0);
        }
        
        // Update sensor body position (for interaction detection)
        // Sensor body stays at the center of the door (same as collision body center)
        if (this.sensorBody && this.physics) {
            // Keep sensor at door's center position (same as collision body)
            this.physics.setPosition(this.sensorBody, this.x, this.y);
        }

        if (this.isAnimating) {
            this.animationTime += dt;
            const progress = Math.min(this.animationTime / this.animationDuration, 1);
            
            const easedProgress = easeOutElastic(progress);
            const startAngle = this.isOpen ? 0 : 70;
            
            if (this.overshoot !== 0) {
                const overshootAngle = this.targetAngle + (this.isOpen ? this.overshoot : -this.overshoot);
                if (progress < 0.7) {
                    this.doorAngle = lerp(startAngle, overshootAngle, progress / 0.7);
                } else {
                    this.doorAngle = lerp(overshootAngle, this.targetAngle, (progress - 0.7) / 0.3);
                }
            } else {
                this.doorAngle = lerp(startAngle, this.targetAngle, easedProgress);
            }
            
            if (progress >= 1) {
                this.doorAngle = this.targetAngle;
                this.isAnimating = false;
            }
        }
    }

    draw(ctx, camera) {
        if (this.isBroken) return;

        ctx.save();
        
        const drawX = this.x;
        const drawY = this.y;
        
        // --- 3/4 PERSPECTIVE SETTINGS ---
        const depth = 5;
        // For vertical doors, width is the vertical dimension, so use width for heightOffset
        const heightOffset = (this.orientation === 'horizontal' ? this.height : this.width) * 0.3; // Door thickness
        
        const darkerColor = '#654321';
        const topColor = '#a65d2e';
        const doorColor = CONFIG.colors.cupboard;
        
        // Calculate front face position based on orientation
        let frontY, frontHeight;
        if (this.orientation === 'horizontal') {
            frontY = drawY - this.height / 2 - heightOffset + depth;
            frontHeight = this.height + heightOffset - depth;
        } else {
            // Vertical doors: height is horizontal dimension, width is vertical dimension
            frontY = drawY - this.width / 2 - heightOffset + depth;
            frontHeight = this.width + heightOffset - depth;
        }

        // Hinged door mechanism: two panels with horizontal skew
        // Works for both horizontal and vertical orientations
        const maxAngle = 70;
        const openAmount = Math.abs(this.doorAngle) / maxAngle;
        const skewAngle = openAmount * 25; // 25 degrees max horizontal skew
        
        if (this.orientation === 'horizontal') {
                // Horizontal doors: translate left/right, skew horizontally
                // Use visual door width (50px total, 25px per panel) for sprite rendering
                // Left door panel
                ctx.save();
                const leftDoorClosedX = drawX - this.visualDoorTotalWidth / 2; // Center minus 25px
                const leftDoorOpenX = leftDoorClosedX - this.visualDoorWidth; // Right edge moves to left edge position
                const leftDoorX = lerp(leftDoorClosedX, leftDoorOpenX, openAmount);
                // Move down only when open (for alignment)
                const leftDoorY = lerp(frontY, frontY + this.visualDoorWidth / 2, openAmount);
                
                // Apply horizontal skew
                ctx.translate(leftDoorX, leftDoorY);
                ctx.transform(1, -Math.tan(skewAngle * Math.PI / 180), 0, 1, 0, 0); // Negative for downward skew
                
                ctx.fillStyle = doorColor;
                ctx.fillRect(0, 0, this.visualDoorWidth, frontHeight);
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 2;
                ctx.strokeRect(0, 0, this.visualDoorWidth, frontHeight);
                ctx.fillStyle = darkerColor;
                ctx.fillRect(0, 0, 1, frontHeight); // Left edge
                // Handle
                ctx.fillStyle = '#FFD700';
                ctx.beginPath(); ctx.arc(this.visualDoorWidth - 4, frontHeight / 2, 2, 0, Math.PI * 2); ctx.fill();
                ctx.restore();

                // Right door panel
                ctx.save();
                const rightDoorClosedX = drawX + this.visualDoorTotalWidth / 2 - this.visualDoorWidth; // Center plus 25px minus 25px = center
                const rightDoorOpenX = rightDoorClosedX + this.visualDoorWidth; // Left edge moves to right edge position
                const rightDoorX = lerp(rightDoorClosedX, rightDoorOpenX, openAmount);
                
                // Apply horizontal skew
                ctx.translate(rightDoorX, frontY);
                ctx.transform(1, Math.tan(skewAngle * Math.PI / 180), 0, 1, 0, 0); // Positive for downward skew
                
                ctx.fillStyle = doorColor;
                ctx.fillRect(0, 0, this.visualDoorWidth, frontHeight);
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 2;
                ctx.strokeRect(0, 0, this.visualDoorWidth, frontHeight);
                ctx.fillStyle = darkerColor;
                ctx.fillRect(this.visualDoorWidth - 1, 0, 1, frontHeight); // Right edge
                // Handle
                ctx.fillStyle = '#FFD700';
                ctx.beginPath(); ctx.arc(4, frontHeight / 2, 2, 0, Math.PI * 2); ctx.fill();
                ctx.restore();
            } else {
                // Vertical doors: same hinged mechanism (left/right panels)
                // Use visual door width (50px total, 25px per panel) for sprite rendering
                // Left door panel
                ctx.save();
                const leftDoorClosedX = drawX - this.visualDoorTotalWidth / 2; // Center minus 25px
                const leftDoorOpenX = leftDoorClosedX - this.visualDoorWidth;
                const leftDoorX = lerp(leftDoorClosedX, leftDoorOpenX, openAmount);
                // Move down only when open (for alignment)
                const leftDoorY = lerp(frontY, frontY + this.visualDoorWidth / 2, openAmount);
                
                // Apply horizontal skew
                ctx.translate(leftDoorX, leftDoorY);
                ctx.transform(1, -Math.tan(skewAngle * Math.PI / 180), 0, 1, 0, 0); // Negative for downward skew
                
                ctx.fillStyle = doorColor;
                ctx.fillRect(0, 0, this.visualDoorWidth, frontHeight);
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 2;
                ctx.strokeRect(0, 0, this.visualDoorWidth, frontHeight);
                ctx.fillStyle = darkerColor;
                ctx.fillRect(0, 0, 1, frontHeight); // Left edge
                // Handle
                ctx.fillStyle = '#FFD700';
                ctx.beginPath(); ctx.arc(this.visualDoorWidth - 4, frontHeight / 2, 2, 0, Math.PI * 2); ctx.fill();
                ctx.restore();

                // Right door panel
                ctx.save();
                const rightDoorClosedX = drawX + this.visualDoorTotalWidth / 2 - this.visualDoorWidth; // Center plus 25px minus 25px = center
                const rightDoorOpenX = rightDoorClosedX + this.visualDoorWidth;
                const rightDoorX = lerp(rightDoorClosedX, rightDoorOpenX, openAmount);
                
                // Apply horizontal skew
                ctx.translate(rightDoorX, frontY);
                ctx.transform(1, Math.tan(skewAngle * Math.PI / 180), 0, 1, 0, 0); // Positive for downward skew
                
                ctx.fillStyle = doorColor;
                ctx.fillRect(0, 0, this.visualDoorWidth, frontHeight);
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 2;
                ctx.strokeRect(0, 0, this.visualDoorWidth, frontHeight);
                ctx.fillStyle = darkerColor;
                ctx.fillRect(this.visualDoorWidth - 1, 0, 1, frontHeight); // Right edge
                // Handle
                ctx.fillStyle = '#FFD700';
                ctx.beginPath(); ctx.arc(4, frontHeight / 2, 2, 0, Math.PI * 2); ctx.fill();
                ctx.restore();
            }
        
        ctx.restore();
    }

    isNearPlayer(player) {
        // Use sensor body from BreakableEntity (positioned below door)
        if (!player || !player.body || !this.sensorBody || !this.physics) return false;
        return this.physics.isPlayerInSensor(this.sensorBody, player.body);
    }

    // Door inherits drawUI from BreakableEntity - no override needed
}
